import { Clock, Phone, PhoneOff, Sparkles, TimerReset, UserCheck } from 'lucide-react';
import type { MetricCardTheme } from 'common/components';

export type InfoData = string | number | Array<unknown> | { value?: unknown; count?: number } | null | undefined;

export const DASHBOARD_METRIC_THEME_BASE: Omit<MetricCardTheme, 'icon'> = {
    color: 'var(--slai-ui-primary-purple-dark)',
    softBackground: 'var(--slai-ui-primary-purple-light)',
};

const toId = (value: string): string =>
    value
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');

export const getDashboardMetricId = (title: string, key?: string): string => toId(key || title) || 'metric';

export const getDashboardMetricTheme = (title: string, key?: string): MetricCardTheme => {
    const id = `${key ?? ''} ${title}`.toLowerCase();

    if (id.includes('missed')) {
        return { ...DASHBOARD_METRIC_THEME_BASE, icon: PhoneOff };
    }
    if (id.includes('answered') || id.includes('hot')) {
        return { ...DASHBOARD_METRIC_THEME_BASE, icon: UserCheck };
    }
    if (id.includes('duration') || id.includes('avg')) {
        return { ...DASHBOARD_METRIC_THEME_BASE, icon: TimerReset };
    }
    if (id.includes('ai')) {
        return { ...DASHBOARD_METRIC_THEME_BASE, icon: Sparkles };
    }
    if (id.includes('time')) {
        return { ...DASHBOARD_METRIC_THEME_BASE, icon: Clock };
    }

    return { ...DASHBOARD_METRIC_THEME_BASE, icon: Phone };
};

export const getInfoDisplayValue = (data: InfoData): string => {
    if (data == null) return '0';

    if (Array.isArray(data) && data.length > 0 && data[0] && typeof data[0] === 'object') {
        const firstItem = data[0] as Record<string, unknown>;
        if ('value' in firstItem && Array.isArray(firstItem.value)) {
            return firstItem.value.length.toString();
        }
    }

    if (Array.isArray(data)) {
        return data.length.toString();
    }

    if (typeof data === 'object') {
        if ('value' in data) {
            if (Array.isArray(data.value)) {
                return data.value.length.toString();
            }
            return String(data.value ?? '0');
        }
        if ('count' in data) return String(data.count ?? '0');
        return '0';
    }

    return String(data);
};

export const formatCompactDuration = (totalSeconds: number | null | undefined): string => {
    const secondsTotal = Math.max(0, Math.round(totalSeconds || 0));
    if (secondsTotal === 0) return '0s';

    const hours = Math.floor(secondsTotal / 3600);
    const minutes = Math.floor((secondsTotal % 3600) / 60);
    const seconds = secondsTotal % 60;
    const parts: string[] = [];

    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (seconds > 0 || parts.length === 0) parts.push(`${seconds}s`);

    return parts.join(' ');
};
